import { ChangeDetectionStrategy, Component, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
} from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Organisations } from 'server/shared/collections/organisations';
import { Customer } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { Organisation } from 'server/shared/models/organisation';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { Tag } from 'server/shared/models/tag';
import { User } from 'server/shared/models/user';
import { observeTracker } from 'src/helpers/meteorObservable/observeTracker';
import { rxDistinctJSONStringify } from 'src/helpers/rxDistinctJSONStringify';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { CustomerService } from '../services/customer.service';
import { ToastService } from '../services/toast/toast.service';
import { UserService } from '../services/user.service';
import { SubscriptionService } from '../shared/subscription.service';

interface DepartmentSettings extends Department {
  collapsed: boolean;
  defaultValues: {
    [key in ShiftEnum]: number;
  };
  appliedValues: {
    [key in ShiftEnum]: number;
  };
  timeTrackingAppliedPause: {
    [key in ShiftEnum]?: number;
  };
  timeTrackingDefaultPause: {
    [key in ShiftEnum]: number;
  };
}

interface OrganisationSettings extends Organisation {
  departments: DepartmentSettings[];
  // collapsed: boolean;
}

@Component({
  selector: 'organisation-index',
  templateUrl: 'organisation-index.html',
  styleUrls: ['organisation-index.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganisationIndexComponent {
  loggedInUser?: User;

  currentTimeTrackingEnabledState: boolean = false;

  customer$ = this.customerService.readyDispoCustomer$.pipe(shareReplay(1));
  customerId$ = this.customer$.pipe(
    map((customer) => customer?._id),
    distinctUntilChanged(),
  );

  shiftEnum = ShiftEnum;

  loggedInUser$ = this.userService.readyDispo$;

  searchTextInputValue$ = new BehaviorSubject<string>('');
  searchText$ = this.searchTextInputValue$.pipe(
    distinctUntilChanged(),
    shareReplay(1),
  );

  searchQuery$ = combineLatest([this.customerId$, this.searchText$]).pipe(
    map(([customerId, searchText]) => {
      if (!searchText) {
        return;
      }
      const filters: Record<string, any> = {
        $or: [],
      };
      let searchTexts = searchText.replace('+', '\\+').split(' ');
      searchTexts.forEach((searchText) => {
        filters['$or'].push({
          $or: [
            {
              name: {
                $regex: searchText,
                $options: 'i',
              },
            },
            {
              'departments.name': {
                $regex: searchText,
                $options: 'i',
              },
            },
            {
              [`customerInformation.${customerId}.tags.displayName`]: {
                $regex: searchText,
                $options: 'i',
              },
            },
          ],
        });
      });
      return filters;
    }),
  );

  customerTimeTrackingOrganisationBlacklist$ = this.customer$.pipe(
    map(
      (customer) => new Set(customer?.timeTrackingOrganisationBlacklist) ?? [],
    ),
    rxDistinctJSONStringify(),
  );

  customerPreventAutoDirectAcceptWithinHours$ = this.customer$.pipe(
    map((customer) => {
      const defaultValues = {
        [ShiftEnum.EARLY]: 0,
        [ShiftEnum.DAY]: 0,
        [ShiftEnum.LATE]: 0,
        [ShiftEnum.NIGHT]: 0,
      };
      if (customer?.preventDirectAcceptWithinSecondsPerShift) {
        defaultValues[ShiftEnum.EARLY] =
          (customer.preventDirectAcceptWithinSecondsPerShift[ShiftEnum.EARLY] ||
            0) / 3600;
        defaultValues[ShiftEnum.DAY] =
          (customer.preventDirectAcceptWithinSecondsPerShift[ShiftEnum.DAY] ||
            0) / 3600;
        defaultValues[ShiftEnum.LATE] =
          (customer.preventDirectAcceptWithinSecondsPerShift[ShiftEnum.LATE] ||
            0) / 3600;
        defaultValues[ShiftEnum.NIGHT] =
          (customer.preventDirectAcceptWithinSecondsPerShift[ShiftEnum.NIGHT] ||
            0) / 3600;
      }
      return defaultValues;
    }),
    rxDistinctJSONStringify(),
  );

  organisationsLoading$ = this.subscriptionService.organisations.loading$.pipe(
    rxNgZoneRun(this.ngZone),
    shareReplay(1),
  );

  rawOrganisationsByQuery$ = combineLatest([
    this.subscriptionService.organisations.ready$,
    this.searchQuery$,
  ]).pipe(
    switchMap(([ready, searchQuery]) =>
      observeTracker(() => {
        if (!ready) {
          return [];
        }
        return Organisations.find(searchQuery || {}, {
          sort: { name: 1 },
        }).fetch();
      }),
    ),
  );

  ownerCustomers$ = combineLatest([
    this.rawOrganisationsByQuery$,
    this.subscriptionService.transferCustomer.ready$,
    this.subscriptionService.sharedPoolCustomers.ready$,
    this.subscriptionService.organisationOwnerCustomers.ready$,
  ]).pipe(
    map(([organisations, transferCustomerReady, sharedPoolCustomersReady]) => {
      return organisations.flatMap((organisation) => {
        return (
          organisation.ownerCustomerIds?.map((ownerCustomerId) =>
            Customers.findOne({ _id: ownerCustomerId }),
          ) ?? []
        );
      });
    }),
    rxDistinctJSONStringify(),
  );

  organisations$ = combineLatest([
    this.rawOrganisationsByQuery$,
    this.customerTimeTrackingOrganisationBlacklist$,
    this.customerPreventAutoDirectAcceptWithinHours$,
    this.customer$,
    this.ownerCustomers$,
  ]).pipe(
    map(
      ([organisations, blacklist, defaultValues, customer, ownerCustomers]) => {
        return organisations.map((organisation): OrganisationSettings => {
          let timeTrackingEnabledByOwner = ownerCustomers.some(
            (ownerCustomer) =>
              ownerCustomer._id !== customer?._id &&
              organisation.ownerCustomerIds?.includes(ownerCustomer._id) &&
              ownerCustomer.timeTrackingEnabled,
          );

          return {
            ...organisation,
            timeTrackingEnabled: timeTrackingEnabledByOwner
              ? true
              : !blacklist.has(organisation._id),
            timeTrackingEnabledByOwner: timeTrackingEnabledByOwner,
            departments: organisation.departments.map(
              (department): DepartmentSettings => {
                const customerId = customer?._id;
                const departmentValues =
                  department.customerInformation?.[customerId!]
                    ?.preventDirectAcceptWithinSecondsPerShift;
                const { earlyValue, dayValue, lateValue, nightValue } = {
                  earlyValue: departmentValues?.[ShiftEnum.EARLY],
                  dayValue: departmentValues?.[ShiftEnum.DAY],
                  lateValue: departmentValues?.[ShiftEnum.LATE],
                  nightValue: departmentValues?.[ShiftEnum.NIGHT],
                };
                const appliedValues = {
                  [ShiftEnum.EARLY]: earlyValue
                    ? earlyValue / 3600
                    : defaultValues[ShiftEnum.EARLY],
                  [ShiftEnum.DAY]: dayValue
                    ? dayValue / 3600
                    : defaultValues[ShiftEnum.DAY],
                  [ShiftEnum.LATE]: lateValue
                    ? lateValue / 3600
                    : defaultValues[ShiftEnum.LATE],
                  [ShiftEnum.NIGHT]: nightValue
                    ? nightValue / 3600
                    : defaultValues[ShiftEnum.NIGHT],
                };
                const timeTrackingDefaultPause = {
                  [ShiftEnum.EARLY]:
                    customer?.timeTrackingDefaultPauseInHours?.[
                      ShiftEnum.EARLY
                    ] || 0,
                  [ShiftEnum.DAY]:
                    customer?.timeTrackingDefaultPauseInHours?.[
                      ShiftEnum.DAY
                    ] || 0,
                  [ShiftEnum.LATE]:
                    customer?.timeTrackingDefaultPauseInHours?.[
                      ShiftEnum.LATE
                    ] || 0,
                  [ShiftEnum.NIGHT]:
                    customer?.timeTrackingDefaultPauseInHours?.[
                      ShiftEnum.NIGHT
                    ] || 0,
                };

                const timeTrackingDefaultPauseByDepartment =
                  customer?.timeTrackingDefaultPauseByDepartment?.[
                    department.id
                  ] || {};

                const timeTrackingAppliedPause = {
                  [ShiftEnum.EARLY]:
                    timeTrackingDefaultPauseByDepartment?.[ShiftEnum.EARLY] !==
                    undefined
                      ? timeTrackingDefaultPauseByDepartment[ShiftEnum.EARLY]
                      : timeTrackingDefaultPause[ShiftEnum.EARLY] || 0,
                  [ShiftEnum.DAY]:
                    timeTrackingDefaultPauseByDepartment?.[ShiftEnum.DAY] !==
                    undefined
                      ? timeTrackingDefaultPauseByDepartment[ShiftEnum.DAY]
                      : timeTrackingDefaultPause[ShiftEnum.DAY] || 0,
                  [ShiftEnum.LATE]:
                    timeTrackingDefaultPauseByDepartment?.[ShiftEnum.LATE] !==
                    undefined
                      ? timeTrackingDefaultPauseByDepartment[ShiftEnum.LATE]
                      : timeTrackingDefaultPause[ShiftEnum.LATE] || 0,
                  [ShiftEnum.NIGHT]:
                    timeTrackingDefaultPauseByDepartment?.[ShiftEnum.NIGHT] !==
                    undefined
                      ? timeTrackingDefaultPauseByDepartment[ShiftEnum.NIGHT]
                      : timeTrackingDefaultPause[ShiftEnum.NIGHT] || 0,
                };

                return {
                  ...department,
                  collapsed: true,
                  defaultValues,
                  appliedValues,
                  timeTrackingAppliedPause,
                  timeTrackingDefaultPause,
                };
              },
            ),
            // collapsed: true,
          };
        });
      },
    ),
    rxNgZoneRun(this.ngZone),
    shareReplay(1),
  );

  constructor(
    private toastService: ToastService,
    private ngZone: NgZone,
    private dialog: MatDialog,
    private subscriptionService: SubscriptionService,
    private customerService: CustomerService,
    private userService: UserService,
  ) {}

  selectTag(organisation: Organisation, customer: Customer, tagId: string) {
    const tag = customer?.tags?.find((tag) => tag.id === tagId);
    if (!tag) {
      return;
    }
    Meteor.call(
      'addTagToOrganisation',
      organisation._id,
      customer._id,
      tagId,
      (error: Meteor.Error) => {
        if (error?.error === 'tagAlreadyOnOrganisation') {
          this.toastService.add({
            type: 'ERROR',
            message: 'tagAlreadyOnOrganisation',
          });
        }
        if (!error && tag) {
          this.addTagToLocalOrg(organisation, customer, tag);
        }
      },
    );
  }

  removeTag(organisation: Organisation, customer: Customer, tagId: string) {
    Meteor.call(
      'removeTagFromOrganisation',
      organisation._id,
      customer._id,
      tagId,
      (error: Meteor.Error) => {
        if (!customer) {
          return;
        }
        if (!error) {
          let index = organisation.customerInformation?.[
            customer._id
          ]?.tags?.findIndex((tag) => tag.id === tagId);
          if (index === undefined || index === -1) {
            return;
          }
          organisation.customerInformation?.[customer._id]?.tags?.splice(
            index,
            1,
          );
        }
      },
    );
  }

  newTagAdded(organisationId: string, customer: Customer, tagId: string) {
    Meteor.call(
      'addTagToOrganisation',
      organisationId,
      customer._id,
      tagId,
      (error: Meteor.Error) => {
        if (error?.error === 'tagAlreadyOnOrganisation') {
          this.toastService.add({
            type: 'ERROR',
            message: 'tagAlreadyOnOrganisation',
          });
        }
      },
    );
  }

  addTagToLocalOrg(organisation: Organisation, customer: Customer, tag: Tag) {
    if (!organisation.customerInformation) {
      organisation.customerInformation = {};
    }
    if (!organisation.customerInformation[customer._id]) {
      organisation.customerInformation[customer._id] = {
        displayName: customer.name,
        tags: [],
      };
    }
    if (!organisation.customerInformation[customer._id].tags?.length) {
      organisation.customerInformation[customer._id].tags = [];
    }
    organisation.customerInformation[customer._id].tags!.push(tag);
  }

  searchFromInput(searchText: string): void {
    this.searchTextInputValue$.next(searchText);
  }

  trackById(index: number, organisation: Organisation): string {
    return organisation._id;
  }

  trackByDepartmentId(index: number, department: DepartmentSettings): string {
    return department.id;
  }

  toggleTimeTrackingOnOrganisation(
    event: Event,
    organisation: Organisation,
    customer: Customer,
    templateRef: any,
  ) {
    if (organisation.timeTrackingEnabledByOwner) {
      return;
    }
    this.currentTimeTrackingEnabledState = !organisation.timeTrackingEnabled;
    event.preventDefault();
    const dialogRef = this.dialog.open(templateRef);
    dialogRef.afterClosed().subscribe((response) => {
      if (response) {
        organisation.timeTrackingEnabled = !organisation.timeTrackingEnabled;
        Meteor.call(
          'toggleTimeTrackingOnOrganisation',
          organisation._id,
          customer._id,
          (error: Meteor.Error) => {
            if (error) {
              this.toastService.add({
                type: 'ERROR',
                message: error.error?.toString(),
              });
            }
          },
        );
      }
    });
  }

  updateDepartmentAutoBookInvitees(department: Department, customerId: string) {
    if (!department.customerInformation) {
      department.customerInformation = {};
    }
    if (!department.customerInformation[customerId]) {
      department.customerInformation[customerId] = {};
    }
    department.customerInformation[customerId].autoBookInvitees =
      !department.customerInformation[customerId].autoBookInvitees;

    if (
      department.customerInformation[customerId].autoBookSelfSignIns &&
      !department.customerInformation[customerId].autoBookInvitees
    ) {
      department.customerInformation[customerId].autoBookSelfSignIns = false;
    }

    Meteor.call(
      'saveDepartmentCustomerInformation',
      department,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        }
      },
    );
  }

  updateDepartmentAutoBookSelfSignIns(
    department: Department,
    customer: Customer,
  ) {
    const customerId = customer._id;
    if (!department.customerInformation?.[customerId]?.autoBookInvitees) {
      if (customer?.autoBookInviteesDepartmentDefault) {
        this.updateDepartmentAutoBookInvitees(department, customerId);
      } else {
        return;
      }
    }
    if (!department.customerInformation) {
      department.customerInformation = {};
    }
    if (!department.customerInformation?.[customerId]) {
      department.customerInformation[customerId] = {};
    }

    department.customerInformation[customerId].autoBookSelfSignIns =
      !department.customerInformation[customerId].autoBookSelfSignIns;

    Meteor.call(
      'saveDepartmentCustomerInformation',
      department,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        }
      },
    );
  }

  private debounceTimers = new Map<string, NodeJS.Timeout>();

  resetDepartmentPreventDirectAcceptValue(
    departmentSettings: DepartmentSettings,
    customerId: string,
    shift: ShiftEnum,
  ) {
    const defaultValue = departmentSettings.defaultValues[shift];
    this.updateDepartmentPreventDirectAcceptWithinSecondsPerShift(
      departmentSettings,
      customerId,
      shift,
      defaultValue,
    );
  }

  updateDepartmentPreventDirectAcceptWithinSecondsPerShift(
    departmentSettings: DepartmentSettings,
    customerId: string,
    shift: ShiftEnum,
    value: number,
  ) {
    const defaultValueInHours = departmentSettings.defaultValues[shift];
    const defaultValueInSeconds = defaultValueInHours * 3600;
    const newValueInSeconds = value * 3600;

    // If the new value equals the default, we should unset/delete it
    const shouldUnset = newValueInSeconds === defaultValueInSeconds;

    const department = departmentSettings as Department;

    if (!department.customerInformation) {
      department.customerInformation = {};
    }
    if (!department.customerInformation?.[customerId]) {
      department.customerInformation[customerId] = {};
    }

    if (shouldUnset) {
      // Remove the value so it falls back to default
      if (
        department.customerInformation[customerId]
          ?.preventDirectAcceptWithinSecondsPerShift
      ) {
        delete department.customerInformation[customerId]
          .preventDirectAcceptWithinSecondsPerShift![shift];
        // If all shifts are unset, remove the entire object
        const customerInfo = department.customerInformation[customerId];
        const remainingShifts = Object.keys(
          customerInfo?.preventDirectAcceptWithinSecondsPerShift || {},
        ).filter((key) => {
          const shiftKey = key as unknown as ShiftEnum;
          return (
            customerInfo?.preventDirectAcceptWithinSecondsPerShift?.[
              shiftKey
            ] !== undefined
          );
        });
        if (remainingShifts.length === 0) {
          delete department.customerInformation[customerId]
            .preventDirectAcceptWithinSecondsPerShift;
        }
      }
      // Update appliedValues to reflect the default
      departmentSettings.appliedValues[shift] = defaultValueInHours;
    } else {
      // Set the new value
      if (
        !department.customerInformation?.[customerId]
          ?.preventDirectAcceptWithinSecondsPerShift
      ) {
        department.customerInformation![
          customerId!
        ].preventDirectAcceptWithinSecondsPerShift = {
          [ShiftEnum.EARLY]: 0,
          [ShiftEnum.DAY]: 0,
          [ShiftEnum.LATE]: 0,
          [ShiftEnum.NIGHT]: 0,
        };
      }

      department.customerInformation![
        customerId
      ]!.preventDirectAcceptWithinSecondsPerShift![shift] = newValueInSeconds;
      // Update appliedValues to reflect the new value
      departmentSettings.appliedValues[shift] = value;
    }

    const debounceKey = `${department.id}-${shift}`;

    if (this.debounceTimers.has(debounceKey)) {
      clearTimeout(this.debounceTimers.get(debounceKey)!);
    }

    const timer = setTimeout(() => {
      Meteor.call(
        'updateDepartmentPreventDirectAcceptWithinSecondsPerShift',
        department.id,
        shift,
        shouldUnset ? null : newValueInSeconds,
        (error: Meteor.Error) => {
          if (error) {
            this.toastService.add({
              type: 'ERROR',
              message: error.error?.toString(),
            });
          }
        },
      );
      this.debounceTimers.delete(debounceKey);
    }, 500);

    this.debounceTimers.set(debounceKey, timer);
  }

  resetDepartmentTimeTrackingPauseValue(
    departmentSettings: DepartmentSettings,
    customerId: string,
    shift: ShiftEnum,
  ) {
    const defaultValue = departmentSettings.timeTrackingDefaultPause[shift];
    this.updateDepartmentTimeTrackingPause(
      departmentSettings,
      customerId,
      shift,
      defaultValue,
    );
  }

  updateDepartmentTimeTrackingPause(
    departmentSettings: DepartmentSettings,
    customerId: string,
    shift: ShiftEnum,
    value: number,
  ) {
    const shouldUnset =
      value === departmentSettings.timeTrackingDefaultPause[shift];

    const department = departmentSettings as Department;

    if (shouldUnset) {
      departmentSettings.timeTrackingAppliedPause[shift] =
        departmentSettings.timeTrackingDefaultPause[shift];
    } else {
      departmentSettings.timeTrackingAppliedPause[shift] = value;
    }

    const debounceKey = `${department.id}-${shift}`;

    if (this.debounceTimers.has(debounceKey)) {
      clearTimeout(this.debounceTimers.get(debounceKey)!);
    }

    const timer = setTimeout(() => {
      Meteor.call(
        'updateDepartmentTimeTrackingPause',
        department.id,
        customerId,
        shift,
        shouldUnset ? null : value,
        (error: Meteor.Error) => {
          if (error) {
            this.toastService.add({
              type: 'ERROR',
              message: error.error?.toString(),
            });
          }
        },
      );
      this.debounceTimers.delete(debounceKey);
    }, 500);

    this.debounceTimers.set(debounceKey, timer);
  }
}
